import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal, Table } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import axios from 'axios';

import { cambiarStatusOrden } from '../../../../actions/ordenesSalida';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicKey = (u) => `${u?.area || 'na'}-${u?.organizador || 'null'}-${u?.ubicacion || 'null'}`;

const buildUbicacionLabel = (inv) => {
    const parts = [];
    if (inv?.area?.nombre) parts.push(inv.area.nombre);
    if (inv?.organizador?.nombre) parts.push(inv.organizador.nombre);
    if (inv?.ubicacion?.nombre) parts.push(inv.ubicacion.nombre);
    return parts.join(' / ') || 'Sin ubicación';
};

/* Igual que en la conversión de cotización en venta: el mismo producto puede aparecer como línea
   suelta y, a la vez, como componente de un paquete en la misma orden — ambos casos comparten el
   mismo inventario real. `pool` se pasa por referencia y se descuenta a medida que se asigna, para
   que ninguna combinación producto+ubicación se reserve dos veces entre productos y componentes. */
const autoAsignar = (productoId, cantidad, ubicaciones, pool) => {
    const getPoolKey = (u) => `${productoId}__${buildUbicKey(u)}`;
    const lista = Array.isArray(ubicaciones) ? ubicaciones : [];

    lista.forEach((u) => {
        const k = getPoolKey(u);
        if (!(k in pool)) pool[k] = Number(u.disponible || 0);
    });

    const ordenadas = [...lista].sort((a, b) => pool[getPoolKey(b)] - pool[getPoolKey(a)]);
    const asignacion = {};
    let restante = cantidad;

    ordenadas.forEach((u) => {
        if (restante <= 0) return;
        const k = getPoolKey(u);
        const disponible = pool[k] || 0;
        const usar = Math.min(restante, disponible);
        if (usar > 0) {
            asignacion[buildUbicKey(u)] = usar;
            restante -= usar;
            pool[k] = disponible - usar;
        }
    });

    return asignacion;
};

const EntregarOrdenModal = ({ show, onHide, orden, onSuccess }) => {
    const dispatch = useDispatch();

    const productosOrden = useMemo(() => (Array.isArray(orden?.productos) ? orden.productos : []), [orden]);
    const paquetesOrden = useMemo(() => (Array.isArray(orden?.paquetes) ? orden.paquetes : []), [orden]);

    /* Lista plana de componentes de paquete, con referencia a su paquete padre, para iterarlos de
       forma uniforme junto a los productos sueltos. */
    const componentesOrden = useMemo(() => {
        const lista = [];
        paquetesOrden.forEach((p) => {
            (Array.isArray(p.componentes) ? p.componentes : []).forEach((c) => {
                lista.push({
                    key: `${p._id}__${c._id}`,
                    paqueteOrdenId: p._id,
                    paqueteId: p.paquete?._id || null,
                    paqueteNombre: p.paquete?.nombre || 'Sin definir',
                    productoId: c._id,
                    productoNombre: c.nombre,
                    necesaria: Number(c.cantidad_nominal ?? c.cantidad ?? 0)
                });
            });
        });
        return lista;
    }, [paquetesOrden]);

    /* Cuánto de cada producto/componente ya se entregó en rondas anteriores. Los movimientos de
       componentes de paquete traen `orden_salida_paquete`; se excluyen de lo que cuenta para los
       productos sueltos y viceversa, para no mezclar el avance de ambos aunque sea el mismo producto. */
    const pendientePorProducto = useMemo(() => {
        const movimientos = Array.isArray(orden?.movimientos) ? orden.movimientos : [];
        const entregadoPorProductoId = movimientos.reduce((mapa, mov) => {
            if (mov?.orden_salida_paquete) return mapa;
            const idProducto = mov?.producto?._id;
            if (!idProducto) return mapa;
            mapa[idProducto] = (mapa[idProducto] || 0) + Number(mov.cantidad || 0);
            return mapa;
        }, {});

        const mapa = {};
        productosOrden.forEach((p) => {
            const entregado = entregadoPorProductoId[p.producto?._id] || 0;
            mapa[p._id] = Math.max(0, Number(p.cantidad || 0) - entregado);
        });
        return mapa;
    }, [orden, productosOrden]);

    const pendientePorComponente = useMemo(() => {
        const movimientos = Array.isArray(orden?.movimientos) ? orden.movimientos : [];
        const entregadoPorKey = movimientos.reduce((mapa, mov) => {
            if (!mov?.orden_salida_paquete) return mapa;
            const idProducto = mov?.producto?._id;
            if (!idProducto) return mapa;
            const key = `${mov.orden_salida_paquete}__${idProducto}`;
            mapa[key] = (mapa[key] || 0) + Number(mov.cantidad || 0);
            return mapa;
        }, {});

        const mapa = {};
        componentesOrden.forEach((c) => {
            const entregado = entregadoPorKey[`${c.paqueteOrdenId}__${c.productoId}`] || 0;
            mapa[c.key] = Math.max(0, c.necesaria - entregado);
        });
        return mapa;
    }, [orden, componentesOrden]);

    const [cargandoInventarios, setCargandoInventarios] = useState(false);
    const [ubicacionesPorProducto, setUbicacionesPorProducto] = useState({});
    const [ubicacionesPorComponente, setUbicacionesPorComponente] = useState({});
    const [asignaciones, setAsignaciones] = useState({});
    const [asignacionesComponentes, setAsignacionesComponentes] = useState({});
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!show) {
            setUbicacionesPorProducto({});
            setUbicacionesPorComponente({});
            setAsignaciones({});
            setAsignacionesComponentes({});
            return;
        }

        let cancelado = false;

        const fetchInventarios = async (idProducto) => {
            try {
                const params = new URLSearchParams();
                params.append('id', idProducto);

                const res = await axios.post(
                    `${process.env.REACT_APP_API_ROUTE}/inventario/detalles`,
                    params.toString(),
                    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
                );

                const inventarios = Array.isArray(res.data?.producto?.inventarios) ? res.data.producto.inventarios : [];

                return inventarios.map((inv) => ({
                    area: inv?.area?._id || null,
                    organizador: inv?.organizador?._id || null,
                    ubicacion: inv?.ubicacion?._id || null,
                    label: buildUbicacionLabel(inv),
                    disponible: Number(inv?.cantidad || 0)
                })).filter(u => Number(u.disponible) > 0);
            } catch (error) {
                return [];
            }
        };

        const cargar = async () => {
            setCargandoInventarios(true);

            const mapaProductos = {};
            const mapaComponentes = {};

            await Promise.all([
                ...productosOrden.map(async (p) => {
                    const idProducto = p?.producto?._id;
                    mapaProductos[p._id] = idProducto ? await fetchInventarios(idProducto) : [];
                }),
                ...componentesOrden.map(async (c) => {
                    mapaComponentes[c.key] = c.productoId ? await fetchInventarios(c.productoId) : [];
                })
            ]);

            if (cancelado) return;

            setUbicacionesPorProducto(mapaProductos);
            setUbicacionesPorComponente(mapaComponentes);

            const poolDisponible = {};

            const initProductos = {};
            productosOrden.forEach((p) => {
                const pendiente = pendientePorProducto[p._id] || 0;
                const idProductoReal = p.producto?._id;
                initProductos[p._id] = idProductoReal
                    ? autoAsignar(idProductoReal, pendiente, mapaProductos[p._id], poolDisponible)
                    : {};
            });
            setAsignaciones(initProductos);

            const initComponentes = {};
            componentesOrden.forEach((c) => {
                const pendiente = pendientePorComponente[c.key] || 0;
                initComponentes[c.key] = c.productoId
                    ? autoAsignar(c.productoId, pendiente, mapaComponentes[c.key], poolDisponible)
                    : {};
            });
            setAsignacionesComponentes(initComponentes);

            setCargandoInventarios(false);
        };

        cargar();

        return () => {
            cancelado = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, productosOrden, componentesOrden]);

    /* Demanda total ya reservada por producto real + ubicación, sumando productos sueltos y
       componentes de paquete — es el mismo inventario, así que lo que uno ya reservó no puede
       volver a ofrecerse como disponible en el otro. */
    const demandaGlobalPorProductoUbic = useMemo(() => {
        const map = {};
        const addKey = (productoId, ubicKey, val) => {
            if (!productoId) return;
            const k = `${productoId}__${ubicKey}`;
            map[k] = (map[k] || 0) + Number(val || 0);
        };

        productosOrden.forEach((p) => {
            const asign = asignaciones[p._id] || {};
            Object.entries(asign).forEach(([ubicKey, val]) => addKey(p.producto?._id, ubicKey, val));
        });

        componentesOrden.forEach((c) => {
            const asign = asignacionesComponentes[c.key] || {};
            Object.entries(asign).forEach(([ubicKey, val]) => addKey(c.productoId, ubicKey, val));
        });

        return map;
    }, [productosOrden, componentesOrden, asignaciones, asignacionesComponentes]);

    const disponibleRestante = (productoId, ubic, propio) => {
        if (!productoId) return 0;
        const k = `${productoId}__${buildUbicKey(ubic)}`;
        const otros = Number(demandaGlobalPorProductoUbic[k] || 0) - Number(propio || 0);
        return Math.max(0, Number(ubic.disponible || 0) - otros);
    };

    const estadoPorProducto = useMemo(() => {
        const map = {};

        productosOrden.forEach((p) => {
            const ubicaciones = ubicacionesPorProducto[p._id] || [];
            const pendiente = pendientePorProducto[p._id] || 0;
            const asign = asignaciones[p._id] || {};
            const asignado = Object.values(asign).reduce((acc, v) => acc + Number(v || 0), 0);
            const disponibleTotal = ubicaciones.reduce((acc, u) => acc + Number(u.disponible || 0), 0);

            map[p._id] = {
                ubicaciones,
                pendiente,
                asignado,
                disponibleTotal,
                completo: pendiente > 0 && asignado === pendiente,
                sinStock: disponibleTotal < pendiente
            };
        });

        return map;
    }, [productosOrden, ubicacionesPorProducto, asignaciones, pendientePorProducto]);

    const estadoPorComponente = useMemo(() => {
        const map = {};

        componentesOrden.forEach((c) => {
            const ubicaciones = ubicacionesPorComponente[c.key] || [];
            const pendiente = pendientePorComponente[c.key] || 0;
            const asign = asignacionesComponentes[c.key] || {};
            const asignado = Object.values(asign).reduce((acc, v) => acc + Number(v || 0), 0);
            const disponibleTotal = ubicaciones.reduce((acc, u) => acc + Number(u.disponible || 0), 0);

            map[c.key] = {
                ubicaciones,
                pendiente,
                asignado,
                disponibleTotal,
                completo: pendiente > 0 && asignado === pendiente,
                sinStock: disponibleTotal < pendiente
            };
        });

        return map;
    }, [componentesOrden, ubicacionesPorComponente, asignacionesComponentes, pendientePorComponente]);

    const onChangeAsignacion = (idOrdenProducto, productoId, ubic, rawValue) => {
        const pendiente = pendientePorProducto[idOrdenProducto] || 0;

        setAsignaciones((prev) => {
            const asignActual = prev[idOrdenProducto] || {};
            const key = buildUbicKey(ubic);
            const propio = Number(asignActual[key] || 0);
            const asignadoOtrasFilas = Object.entries(asignActual)
                .filter(([k]) => k !== key)
                .reduce((acc, [, v]) => acc + Number(v || 0), 0);

            const v = rawValue === '' ? 0 : Number(rawValue);
            const maxCompartido = disponibleRestante(productoId, ubic, propio);
            const maxPermitido = Math.max(0, Math.min(maxCompartido, pendiente - asignadoOtrasFilas));
            const clamped = Math.max(0, Math.min(v, maxPermitido));

            return {
                ...prev,
                [idOrdenProducto]: {
                    ...asignActual,
                    [key]: clamped
                }
            };
        });
    };

    const onChangeAsignacionComponente = (componenteKey, productoId, ubic, rawValue) => {
        const pendiente = pendientePorComponente[componenteKey] || 0;

        setAsignacionesComponentes((prev) => {
            const asignActual = prev[componenteKey] || {};
            const key = buildUbicKey(ubic);
            const propio = Number(asignActual[key] || 0);
            const asignadoOtrasFilas = Object.entries(asignActual)
                .filter(([k]) => k !== key)
                .reduce((acc, [, v]) => acc + Number(v || 0), 0);

            const v = rawValue === '' ? 0 : Number(rawValue);
            const maxCompartido = disponibleRestante(productoId, ubic, propio);
            const maxPermitido = Math.max(0, Math.min(maxCompartido, pendiente - asignadoOtrasFilas));
            const clamped = Math.max(0, Math.min(v, maxPermitido));

            return {
                ...prev,
                [componenteKey]: {
                    ...asignActual,
                    [key]: clamped
                }
            };
        });
    };

    /* Una orden sin líneas de producto ni paquetes (solo conceptos) no tiene nada que asignar — se
       confirma directamente como Entregada, sin pasar por inventario ni ubicaciones. */
    const sinProductosNiPaquetes = productosOrden.length === 0 && componentesOrden.length === 0;

    const totalAsignadoGlobal = useMemo(() => {
        const totalProductos = Object.values(estadoPorProducto).reduce((acc, e) => acc + Number(e.asignado || 0), 0);
        const totalComponentes = Object.values(estadoPorComponente).reduce((acc, e) => acc + Number(e.asignado || 0), 0);
        return totalProductos + totalComponentes;
    }, [estadoPorProducto, estadoPorComponente]);

    const seraEntregaCompleta = useMemo(() => {
        const productosCompletos = productosOrden.every((p) => {
            const estado = estadoPorProducto[p._id];
            if (!estado || estado.pendiente <= 0) return true;
            return estado.completo;
        });

        const componentesCompletos = componentesOrden.every((c) => {
            const estado = estadoPorComponente[c.key];
            if (!estado || estado.pendiente <= 0) return true;
            return estado.completo;
        });

        return productosCompletos && componentesCompletos;
    }, [productosOrden, componentesOrden, estadoPorProducto, estadoPorComponente]);

    const puedeConfirmar = sinProductosNiPaquetes
        ? !guardando
        : (!cargandoInventarios && totalAsignadoGlobal > 0);

    const handleConfirmar = async () => {
        if (!puedeConfirmar || !orden?._id) return;

        if (sinProductosNiPaquetes) {
            setGuardando(true);
            const ok = await dispatch(cambiarStatusOrden(orden._id, 2, [], []));
            setGuardando(false);

            if (!ok) return;

            onHide();
            if (typeof onSuccess === 'function') onSuccess();
            return;
        }

        const productosEntrega = [];

        productosOrden.forEach((p) => {
            const pendiente = pendientePorProducto[p._id] || 0;
            if (pendiente <= 0) return;

            const asign = asignaciones[p._id] || {};
            const ubicaciones = ubicacionesPorProducto[p._id] || [];

            ubicaciones.forEach((u) => {
                const cant = Number(asign[buildUbicKey(u)] || 0);
                if (cant <= 0) return;

                productosEntrega.push({
                    producto: p.producto?._id || null,
                    area: u.area,
                    organizador: u.organizador,
                    ubicacion: u.ubicacion,
                    cantidad: cant
                });
            });
        });

        /* Agrupar componentes por paquete para el payload que espera el backend:
           paquetes: [{ paquete, componentes: [{producto, area, organizador, ubicacion, cantidad}] }] */
        const componentesPorPaquete = new Map();

        componentesOrden.forEach((c) => {
            const pendiente = pendientePorComponente[c.key] || 0;
            if (pendiente <= 0) return;

            const asign = asignacionesComponentes[c.key] || {};
            const ubicaciones = ubicacionesPorComponente[c.key] || [];

            ubicaciones.forEach((u) => {
                const cant = Number(asign[buildUbicKey(u)] || 0);
                if (cant <= 0) return;

                if (!componentesPorPaquete.has(c.paqueteId)) {
                    componentesPorPaquete.set(c.paqueteId, []);
                }

                componentesPorPaquete.get(c.paqueteId).push({
                    producto: c.productoId,
                    area: u.area,
                    organizador: u.organizador,
                    ubicacion: u.ubicacion,
                    cantidad: cant
                });
            });
        });

        const paquetesEntrega = Array.from(componentesPorPaquete.entries()).map(([paqueteId, componentes]) => ({
            paquete: paqueteId,
            componentes
        }));

        if (productosEntrega.length === 0 && paquetesEntrega.length === 0) return;

        const nuevoStatus = seraEntregaCompleta ? 2 : 1;

        setGuardando(true);
        const ok = await dispatch(cambiarStatusOrden(orden._id, nuevoStatus, productosEntrega, paquetesEntrega));
        setGuardando(false);

        if (!ok) return;

        onHide();
        if (typeof onSuccess === 'function') onSuccess();
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Entregar órden</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {sinProductosNiPaquetes ? (
                    <div className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Esta orden no tiene productos ni paquetes, solo conceptos — no hay inventario que asignar. Al confirmar, la orden
                        quedará marcada como "Entregada".
                    </div>
                ) : (
                    <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                        Asigna la ubicación real de inventario de la que saldrá cada producto. No es necesario cubrir el total pendiente:
                        lo que quede sin asignar se podrá entregar después y la orden quedará como "Semi-entregada"; si cubres todo lo pendiente,
                        la orden quedará como "Entregada".
                    </div>
                )}

                {!sinProductosNiPaquetes && (cargandoInventarios ? (
                    <div className="d-flex justify-content-center py-4">
                        <div className="spinner-border" role="status" />
                    </div>
                ) : (
                    <>
                        {productosOrden.length > 0 && (
                            <div className="table-responsive mb-4">
                                <Table bordered hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Producto</th>
                                            <th style={{ width: 80 }} className="text-center">Cantidad</th>
                                            <th style={{ width: 80 }} className="text-center">Pendiente</th>
                                            <th style={{ width: 450 }}>Ubicación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productosOrden.map((p) => {
                                            const estado = estadoPorProducto[p._id] || { ubicaciones: [], completo: false, sinStock: true, asignado: 0, pendiente: 0 };
                                            const cantidad = Number(p.cantidad || 0);
                                            const idProductoReal = p.producto?._id;

                                            if (estado.pendiente <= 0) {
                                                return (
                                                    <tr key={p._id} className="table-success">
                                                        <td className="align-middle">{p.producto?.nombre || 'Sin definir'}</td>
                                                        <td className="text-center align-middle">{formatNumber(cantidad)}</td>
                                                        <td className="text-center align-middle">0</td>
                                                        <td className="align-middle text-success">
                                                            <i className="fas fa-check-circle" /> Ya entregado por completo
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            const asign = asignaciones[p._id] || {};
                                            const conFlicto = estado.sinStock;

                                            return (
                                                <tr key={p._id} className={conFlicto ? 'table-danger' : ''}>
                                                    <td className="align-middle">{p.producto?.nombre || 'Sin definir'}</td>
                                                    <td className="text-center align-middle">{formatNumber(cantidad)}</td>
                                                    <td className="text-center align-middle">{formatNumber(estado.pendiente)}</td>
                                                    <td>
                                                        {estado.ubicaciones.length === 0 ? (
                                                            <div className="text-danger" style={{ fontSize: 12 }}>
                                                                Sin existencia disponible para este producto.
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                {estado.ubicaciones.map((u) => {
                                                                    const key = buildUbicKey(u);
                                                                    const restante = disponibleRestante(idProductoReal, u, asign[key] || 0);
                                                                    return (
                                                                        <div key={key} className="d-flex align-items-center gap-2 mb-1">
                                                                            <div style={{ minWidth: 150, fontSize: 12 }}>{u.label}</div>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control form-control-sm"
                                                                                style={{ width: 90 }}
                                                                                value={asign[key] ?? 0}
                                                                                min={0}
                                                                                max={restante}
                                                                                onChange={(e) => onChangeAsignacion(p._id, idProductoReal, u, e.target.value)}
                                                                            />
                                                                            <div className="text-muted" style={{ fontSize: 11 }}>
                                                                                / {formatNumber(restante)}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                                <div className={estado.completo ? 'text-success' : 'text-muted'} style={{ fontSize: 11 }}>
                                                                    Asignado: {formatNumber(estado.asignado)} / {formatNumber(estado.pendiente)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {componentesOrden.length > 0 && (
                            <>
                                <div className="fw-semibold mb-2">Componentes de paquetes</div>
                                <div className="table-responsive">
                                    <Table bordered hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Paquete</th>
                                                <th>Producto</th>
                                                <th style={{ width: 80 }} className="text-center">Pendiente</th>
                                                <th style={{ width: 400 }}>Ubicación</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {componentesOrden.map((c) => {
                                                const estado = estadoPorComponente[c.key] || { ubicaciones: [], completo: false, sinStock: true, asignado: 0, pendiente: 0 };

                                                if (estado.pendiente <= 0) {
                                                    return (
                                                        <tr key={c.key} className="table-success">
                                                            <td className="align-middle">{c.paqueteNombre}</td>
                                                            <td className="align-middle">{c.productoNombre}</td>
                                                            <td className="text-center align-middle">0</td>
                                                            <td className="align-middle text-success">
                                                                <i className="fas fa-check-circle" /> Ya entregado por completo
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                const asign = asignacionesComponentes[c.key] || {};
                                                const conFlicto = estado.sinStock;

                                                return (
                                                    <tr key={c.key} className={conFlicto ? 'table-danger' : ''}>
                                                        <td className="align-middle">{c.paqueteNombre}</td>
                                                        <td className="align-middle">{c.productoNombre}</td>
                                                        <td className="text-center align-middle">{formatNumber(estado.pendiente)}</td>
                                                        <td>
                                                            {estado.ubicaciones.length === 0 ? (
                                                                <div className="text-danger" style={{ fontSize: 12 }}>
                                                                    Sin existencia disponible para este producto.
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    {estado.ubicaciones.map((u) => {
                                                                        const key = buildUbicKey(u);
                                                                        const restante = disponibleRestante(c.productoId, u, asign[key] || 0);
                                                                        return (
                                                                            <div key={key} className="d-flex align-items-center gap-2 mb-1">
                                                                                <div style={{ minWidth: 150, fontSize: 12 }}>{u.label}</div>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    style={{ width: 90 }}
                                                                                    value={asign[key] ?? 0}
                                                                                    min={0}
                                                                                    max={restante}
                                                                                    onChange={(e) => onChangeAsignacionComponente(c.key, c.productoId, u, e.target.value)}
                                                                                />
                                                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                                                    / {formatNumber(restante)}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    <div className={estado.completo ? 'text-success' : 'text-muted'} style={{ fontSize: 11 }}>
                                                                        Asignado: {formatNumber(estado.asignado)} / {formatNumber(estado.pendiente)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </>
                ))}

                {!sinProductosNiPaquetes && !cargandoInventarios && !puedeConfirmar && (
                    <div className="mt-3">
                        <Badge bg="danger">Asigna al menos una cantidad antes de confirmar</Badge>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={guardando}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirmar} disabled={!puedeConfirmar || guardando}>
                    {guardando
                        ? 'Confirmando...'
                        : sinProductosNiPaquetes
                            ? 'Marcar como entregada'
                            : (seraEntregaCompleta ? 'Confirmar entrega total' : 'Confirmar entrega parcial')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EntregarOrdenModal;
